import { useEffect, useMemo, useRef, useState } from "react";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Plus,
    Trash2,
    Loader2,
    GraduationCap,
    Pencil,
    Search,
    X,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "@/lib/api";
import { refreshDegrees } from "@/lib/degreeStore";
import { searchDegrees } from "@/lib/degreeStore";

const LEVELS = ["Bachelor's", "Master's"];

const emptyDraft = () => ({
    id: null,
    name: "",
    abbreviation: "",
    level: "Bachelor's",
    status: "Active",
});

export default function ManageDegrees() {
    const [degrees, setDegrees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [levelFilter, setLevelFilter] = useState("all");

    const [dialogOpen, setDialogOpen] = useState(false);
    const [draft, setDraft] = useState(emptyDraft());
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const [deleteTarget, setDeleteTarget] = useState(null);

    const nameInputRef = useRef(null);

    const fetchAll = async () => {
        try {
            const { data } = await api.get("/degrees");
            setDegrees(data);
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
    }, []);

    const visible = useMemo(() => {
        let rows = degrees;
        if (levelFilter !== "all") {
            rows = rows.filter((d) => d.level === levelFilter);
        }
        if (query.trim()) {
            rows = searchDegrees(rows, query);
        }
        return rows;
    }, [degrees, query, levelFilter]);

    const grouped = useMemo(() => {
        const g = { "Bachelor's": [], "Master's": [] };
        visible.forEach((d) => {
            if (g[d.level]) g[d.level].push(d);
        });
        return g;
    }, [visible]);

    const openCreate = () => {
        setDraft(emptyDraft());
        setError("");
        setDialogOpen(true);
        setTimeout(() => nameInputRef.current?.focus(), 60);
    };

    const openEdit = (d) => {
        setDraft({
            id: d.id,
            name: d.name,
            abbreviation: d.abbreviation,
            level: d.level,
            status: d.status || "Active",
        });
        setError("");
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setDraft(emptyDraft());
        setError("");
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        const payload = {
            name: draft.name.trim(),
            abbreviation: draft.abbreviation.trim(),
            level: draft.level,
            status: draft.status,
        };
        if (!payload.name) {
            setError("Degree name is required.");
            return;
        }
        if (!payload.abbreviation) {
            setError("Abbreviation is required.");
            return;
        }
        // Client-side duplicate hint (case-insensitive) before hitting the server.
        const dup = degrees.find(
            (d) =>
                d.id !== draft.id &&
                (d.name.toLowerCase() === payload.name.toLowerCase() ||
                    d.abbreviation.toLowerCase() ===
                        payload.abbreviation.toLowerCase()),
        );
        if (dup) {
            const same =
                dup.name.toLowerCase() === payload.name.toLowerCase()
                    ? "name"
                    : "abbreviation";
            setError(`A degree with this ${same} already exists.`);
            return;
        }
        setSubmitting(true);
        try {
            if (draft.id) {
                const { data } = await api.put(
                    `/degrees/${draft.id}`,
                    payload,
                );
                setDegrees((rows) =>
                    rows.map((r) => (r.id === draft.id ? data : r)),
                );
                toast.success(`"${data.name}" updated.`);
            } else {
                const { data } = await api.post("/degrees", payload);
                setDegrees((rows) => [...rows, data]);
                toast.success(`"${data.name}" added.`);
            }
            await refreshDegrees();
            closeDialog();
        } catch (err) {
            setError(apiErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    const doDelete = async () => {
        if (!deleteTarget) return;
        try {
            await api.delete(`/degrees/${deleteTarget.id}`);
            setDegrees((rows) =>
                rows.filter((r) => r.id !== deleteTarget.id),
            );
            await refreshDegrees();
            toast.success("Degree deleted.");
        } catch (err) {
            toast.error(apiErrorMessage(err));
        } finally {
            setDeleteTarget(null);
        }
    };

    return (
        <div data-testid="admin-degrees-page">
            <AdminPageHeader
                eyebrow="Modules / Master Data"
                title="Manage Degrees"
                description="Single source of truth for the degree dropdown used in every application form."
                actions={
                    <button
                        onClick={openCreate}
                        data-testid="degree-add-button"
                        className="inline-flex items-center gap-2 px-5 h-11 bg-[#0F1F38] text-[#F7F5F0] text-xs tracking-label uppercase font-semibold hover:bg-[#1A3254] transition-colors"
                    >
                        <Plus size={14} /> Add Degree
                    </button>
                }
            />

            <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
                {/* Filters */}
                <div className="bg-white border border-[#0F1F38]/10 p-4 md:p-5 mb-6 flex flex-col md:flex-row gap-3 md:items-center">
                    <div className="flex-1 flex items-center gap-2 h-11 px-3 border border-[#0F1F38]/20 bg-[#F7F5F0]">
                        <Search size={14} className="text-[#0F1F38]/50" />
                        <input
                            data-testid="degree-search"
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search by name or abbreviation…"
                            className="w-full bg-transparent outline-none text-sm placeholder:text-[#0F1F38]/40"
                        />
                        {query && (
                            <button
                                onClick={() => setQuery("")}
                                aria-label="Clear"
                                data-testid="degree-search-clear"
                                className="text-[#0F1F38]/50 hover:text-[#0F1F38]"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>
                    <Select
                        value={levelFilter}
                        onValueChange={setLevelFilter}
                    >
                        <SelectTrigger
                            data-testid="degree-level-filter"
                            className="h-11 md:w-56 rounded-none border-[#0F1F38]/20 bg-white"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All levels</SelectItem>
                            {LEVELS.map((l) => (
                                <SelectItem key={l} value={l}>
                                    {l} Degrees
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <div
                        className="text-xs text-[#4B5563] md:ml-auto"
                        data-testid="degree-count"
                    >
                        {visible.length} of {degrees.length}
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center gap-3 text-sm text-[#4B5563]">
                        <Loader2 size={16} className="animate-spin" />
                        Loading degrees…
                    </div>
                ) : degrees.length === 0 ? (
                    <div
                        data-testid="degrees-empty"
                        className="bg-white border border-dashed border-[#0F1F38]/20 py-20 px-8 text-center flex flex-col items-center"
                    >
                        <div className="w-12 h-12 flex items-center justify-center bg-[#F7F5F0] border border-[#0F1F38]/10 mb-6">
                            <GraduationCap
                                size={20}
                                strokeWidth={1.5}
                                className="text-[#0F1F38]"
                            />
                        </div>
                        <div className="font-display text-3xl leading-tight text-[#0F1F38]">
                            No degrees yet.
                        </div>
                    </div>
                ) : visible.length === 0 ? (
                    <div
                        data-testid="degrees-no-match"
                        className="bg-white border border-dashed border-[#0F1F38]/20 py-16 px-8 text-center text-sm text-[#0F1F38]/60"
                    >
                        No matching degree found
                    </div>
                ) : (
                    <div className="space-y-8">
                        {LEVELS.map((level) => {
                            const rows = grouped[level] || [];
                            if (!rows.length) return null;
                            return (
                                <section key={level}>
                                    <div className="flex items-baseline justify-between mb-3">
                                        <div className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                                            {level} Degrees
                                        </div>
                                        <div className="text-xs text-[#4B5563]">
                                            {rows.length}
                                        </div>
                                    </div>
                                    <div
                                        data-testid={`degree-list-${level.replace(/[^a-z]/gi, "")}`}
                                        className="bg-white border border-[#0F1F38]/10 divide-y divide-[#0F1F38]/10"
                                    >
                                        {rows.map((d) => (
                                            <div
                                                key={d.id}
                                                data-testid={`degree-row-${d.id}`}
                                                className="flex items-center gap-4 px-4 md:px-5 py-3"
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm text-[#0F1F38] truncate">
                                                        {d.name}
                                                    </div>
                                                </div>
                                                <div className="w-40 text-xs font-mono text-[#0F1F38]/70 truncate">
                                                    {d.abbreviation}
                                                </div>
                                                <div
                                                    className={`text-[10px] tracking-label uppercase px-2 py-1 border ${
                                                        d.status === "Active"
                                                            ? "border-[#0F1F38]/20 text-[#0F1F38]"
                                                            : "border-[#0F1F38]/10 text-[#0F1F38]/40"
                                                    }`}
                                                >
                                                    {d.status || "Active"}
                                                </div>
                                                <button
                                                    onClick={() => openEdit(d)}
                                                    data-testid={`degree-edit-${d.id}`}
                                                    className="inline-flex items-center gap-1 px-3 h-8 border border-[#0F1F38]/20 hover:border-[#0F1F38] text-[10px] tracking-label uppercase font-semibold transition-colors"
                                                >
                                                    <Pencil size={11} /> Edit
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        setDeleteTarget(d)
                                                    }
                                                    data-testid={`degree-delete-${d.id}`}
                                                    className="inline-flex items-center gap-1 px-3 h-8 border border-[#0F1F38]/20 hover:border-red-700 hover:text-red-700 text-[10px] tracking-label uppercase font-semibold transition-colors"
                                                >
                                                    <Trash2 size={11} /> Delete
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Create / Edit dialog */}
            <Dialog
                open={dialogOpen}
                onOpenChange={(o) => (o ? setDialogOpen(true) : closeDialog())}
            >
                <DialogContent
                    data-testid="degree-dialog"
                    className="bg-[#F7F5F0] border-[#0F1F38]/20 rounded-none max-w-lg"
                >
                    <DialogHeader>
                        <div className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                            {draft.id ? "Edit degree" : "New degree"}
                        </div>
                        <DialogTitle className="font-display text-2xl leading-tight text-[#0F1F38]">
                            {draft.id
                                ? "Edit degree"
                                : "Add degree"}
                        </DialogTitle>
                        <DialogDescription className="text-sm text-[#4B5563]">
                            Duplicate names or abbreviations are not allowed
                            (case-insensitive).
                        </DialogDescription>
                    </DialogHeader>
                    <form
                        onSubmit={submit}
                        data-testid="degree-form"
                        className="space-y-4"
                    >
                        <div>
                            <Label className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                                Degree Name
                            </Label>
                            <Input
                                ref={nameInputRef}
                                value={draft.name}
                                onChange={(e) =>
                                    setDraft((d) => ({
                                        ...d,
                                        name: e.target.value,
                                    }))
                                }
                                placeholder="e.g. Bachelor of Engineering"
                                data-testid="degree-input-name"
                                className="mt-2 h-11 rounded-none border-[#0F1F38]/20 bg-white"
                            />
                        </div>
                        <div>
                            <Label className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                                Abbreviation
                            </Label>
                            <Input
                                value={draft.abbreviation}
                                onChange={(e) =>
                                    setDraft((d) => ({
                                        ...d,
                                        abbreviation: e.target.value,
                                    }))
                                }
                                placeholder="e.g. BE"
                                data-testid="degree-input-abbr"
                                className="mt-2 h-11 rounded-none border-[#0F1F38]/20 bg-white"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                                    Level
                                </Label>
                                <Select
                                    value={draft.level}
                                    onValueChange={(v) =>
                                        setDraft((d) => ({ ...d, level: v }))
                                    }
                                >
                                    <SelectTrigger
                                        data-testid="degree-input-level"
                                        className="mt-2 h-11 rounded-none border-[#0F1F38]/20 bg-white"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {LEVELS.map((l) => (
                                            <SelectItem key={l} value={l}>
                                                {l}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                                    Status
                                </Label>
                                <div className="mt-2 h-11 flex items-center gap-3 px-3 border border-[#0F1F38]/20 bg-white">
                                    <Switch
                                        checked={draft.status === "Active"}
                                        onCheckedChange={(v) =>
                                            setDraft((d) => ({
                                                ...d,
                                                status: v
                                                    ? "Active"
                                                    : "Inactive",
                                            }))
                                        }
                                        data-testid="degree-input-status"
                                    />
                                    <span className="text-sm text-[#0F1F38]">
                                        {draft.status}
                                    </span>
                                </div>
                            </div>
                        </div>
                        {error && (
                            <div
                                data-testid="degree-form-error"
                                className="text-sm text-red-700"
                            >
                                {error}
                            </div>
                        )}
                        <DialogFooter className="flex items-center justify-end gap-3 pt-2">
                            <button
                                type="button"
                                onClick={closeDialog}
                                data-testid="degree-dialog-cancel"
                                className="px-5 h-11 text-xs tracking-label uppercase font-semibold border border-[#0F1F38]/20 hover:border-[#0F1F38] transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                data-testid="degree-dialog-submit"
                                disabled={submitting}
                                className="px-6 h-11 bg-[#0F1F38] text-[#F7F5F0] text-xs tracking-label uppercase font-semibold hover:bg-[#1A3254] transition-colors disabled:opacity-60 inline-flex items-center gap-2"
                            >
                                {submitting && (
                                    <Loader2
                                        size={14}
                                        className="animate-spin"
                                    />
                                )}
                                {draft.id ? "Save" : "Add"}
                            </button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <Dialog
                open={!!deleteTarget}
                onOpenChange={(o) => !o && setDeleteTarget(null)}
            >
                <DialogContent
                    data-testid="degree-delete-dialog"
                    className="bg-[#F7F5F0] border-[#0F1F38]/20 rounded-none max-w-md"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl leading-tight text-[#0F1F38]">
                            Delete degree?
                        </DialogTitle>
                        <DialogDescription className="text-sm text-[#0F1F38]/80 pt-2">
                            This will permanently remove{" "}
                            <strong>
                                {deleteTarget?.name}
                                {deleteTarget?.abbreviation
                                    ? ` (${deleteTarget.abbreviation})`
                                    : ""}
                            </strong>{" "}
                            from every dropdown across the app. Existing
                            applicant records that already stored this degree
                            remain unchanged.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex items-center justify-end gap-3 pt-4">
                        <button
                            onClick={() => setDeleteTarget(null)}
                            data-testid="degree-delete-cancel"
                            className="px-5 h-11 text-xs tracking-label uppercase font-semibold border border-[#0F1F38]/20 hover:border-[#0F1F38] transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={doDelete}
                            data-testid="degree-delete-confirm"
                            className="px-6 h-11 bg-red-700 text-white text-xs tracking-label uppercase font-semibold hover:bg-red-800 transition-colors"
                        >
                            Delete
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
